import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Divider, Typography, theme } from "antd";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { EProductPlanPaymentInterval } from "@devowl-wp/api-real-commerce";
import { computeOrderSummary } from "@devowl-wp/real-commerce-order-summary";
import { COUPON_FORM_VALUE_KEY, CouponForm, CouponTrigger } from "./CouponInput.js";
import { useStores } from "../../../../store/stores.js";
import { formatAmount } from "../../../../utils/formatAmount.js";
import { __ } from "../../../../utils/i18n.js";
import { CheckoutStep } from "../../store/CheckoutV2Store.js";
import { markdownBold } from "../../utils/markdownBold.js";
const OrderSummary = observer(()=>{
    const { checkoutV2Store } = useStores();
    const { product, transaction } = checkoutV2Store;
    const { token } = theme.useToken();
    if (!product) {
        return null;
    }
    const { data: { productPlan: { product: { name }, description: planDescription, price: [{ currency, initialPrice: listInitialPrice, recurringPrice: listRecurringPrice }] } }, labelPaymentInterval } = product;
    const cashbackPromotion = transaction?.incompleteCheckout?.checkout.cashbackPromotion;
    const hasActiveIdentification = !!transaction?.identification;
    // The iso package is purely structured — every locale-, DOM-, or React-
    // dependent transformation (decimal separator, currency symbol, payment-
    // interval label, `__()` strings, `markdownBold` wrapping) lives here in
    // the WP-side adapter. Numeric values arrive as TypeORM `decimal` strings
    // and are coerced to Number defensively at the iso boundary too.
    const summary = computeOrderSummary({
        currency,
        originalInitialPrice: Number(listInitialPrice),
        originalRecurringPrice: Number(listRecurringPrice),
        paymentInterval: product.data.productPlan.paymentInterval,
        hasActiveIdentification,
        initialPrice: transaction?.initialPrice,
        initialPriceTax: transaction?.initialPriceTax,
        recurringPrice: transaction?.recurringPrice,
        recurringPriceTax: transaction?.recurringPriceTax,
        preCouponInitialPrice: transaction?.preCouponInitialPrice,
        preCouponRecurringPrice: transaction?.preCouponRecurringPrice,
        hasCouponApplied: !!transaction?.hasAppliedCoupon,
        cashbackPromotion: cashbackPromotion ? {
            name: cashbackPromotion.name
        } : undefined
    });
    const isOneTime = summary.paymentInterval === EProductPlanPaymentInterval.OneTime;
    const intervalSuffix = isOneTime ? "" : ` / ${labelPaymentInterval}`;
    const formatPrice = (amount)=>formatAmount({
            amount,
            currency: summary.currency
        });
    const formattedTotalPrice = formatPrice(summary.totalAmount);
    const discountLine = typeof summary.couponDiscountAmount === "number" ? formatAmount({
        amount: summary.couponDiscountAmount,
        currency: summary.currency,
        prefix: "-"
    }) : undefined;
    let taxLabel;
    let taxAmountLine;
    if (summary.taxState.kind === "vat-charged") {
        taxLabel = __("Including VAT (%s)", `${summary.taxState.ratePercent}%`);
        taxAmountLine = formatAmount({
            amount: summary.taxState.amount,
            currency: summary.currency
        });
    } else if (summary.taxState.kind === "reverse-charge") {
        taxLabel = __("Reverse charge applied");
        taxAmountLine = undefined;
    } else {
        taxLabel = __("Including VAT");
        taxAmountLine = undefined;
    }
    const totalLabel = summary.totalIsPaidToday ? __("Total today") : __("Total");
    // Assemble the renewal-disclosure line locally — the iso package returns
    // raw amounts, the WP-side carries `__()` and `formatAmount`.
    const recurringDisclosureLine = summary.recurringDisclosure ? __("in the first %s, afterwards %s%s per %s", labelPaymentInterval, formatAmount({
        amount: summary.recurringDisclosure.recurringAmount,
        currency: summary.currency
    }), typeof summary.recurringDisclosure.recurringVatAmount === "number" ? __(" (incl. %s VAT)", formatAmount({
        amount: summary.recurringDisclosure.recurringVatAmount,
        currency: summary.currency
    })) : "", labelPaymentInterval) : "";
    const cashbackNoticeNode = summary.cashbackPromotionName ? markdownBold(__("You're taking part in the **%s** cashback promotion.", summary.cashbackPromotionName)) : null;
    const isAfterIdentification = checkoutV2Store.step >= CheckoutStep.IdentificationSubmitted;
    // Initialize expanded when a coupon is already in the shared bag — either
    // from the `?coupon=` query param (seeded by `useCheckoutV2Transaction`)
    // or from the localStorage restore pathway (seeded by
    // `useRecoverableFormV2`). Both seed synchronously during render so the
    // first paint already shows the pre-filled coupon field instead of the
    // generic "Got a coupon?" trigger.
    const hasSeededCoupon = !!checkoutV2Store.formValues[COUPON_FORM_VALUE_KEY];
    const [couponExpanded, setCouponExpanded] = useState(hasSeededCoupon);
    // On the Payment step Paddle's own "Add Discount" link takes over — drop
    // our trigger/form there to avoid a duplicate entry point.
    const showCouponTrigger = !isAfterIdentification && !couponExpanded;
    const showCouponForm = !isAfterIdentification && couponExpanded;
    // Once the buyer moves to Payment, unmount the expanded coupon wrapper
    // entirely — Paddle's own "Add Discount" link takes over and a collapsed
    // but still-mounted grid wrapper would leave a subtle empty row below the
    // product line. On return to edit mode (Edit Details) the wrapper is
    // re-expanded so the seeded coupon surfaces again without the buyer
    // having to re-click "Got a coupon?".
    useEffect(()=>{
        if (isAfterIdentification) {
            setCouponExpanded(false);
        } else if (hasSeededCoupon) {
            setCouponExpanded(true);
        }
    }, [
        isAfterIdentification,
        hasSeededCoupon
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: "flex",
                    alignItems: "baseline",
                    flexWrap: "wrap",
                    marginBottom: 12,
                    gap: 12
                },
                children: [
                    /*#__PURE__*/ _jsx(Typography.Title, {
                        level: 5,
                        className: "checkout-v2-section-heading",
                        style: {
                            margin: 0
                        },
                        children: __("Your Order")
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: `checkout-v2-soft-fade${showCouponTrigger ? "" : " checkout-v2-soft-fade--hidden"}`,
                        "aria-hidden": !showCouponTrigger,
                        style: {
                            marginLeft: "auto",
                            whiteSpace: "nowrap"
                        },
                        children: /*#__PURE__*/ _jsx(CouponTrigger, {
                            onClick: ()=>setCouponExpanded(true)
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-product-row",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "checkout-v2-product-row__name",
                        children: [
                            name,
                            planDescription && /*#__PURE__*/ _jsx("span", {
                                className: "checkout-v2-product-row__plan",
                                style: {
                                    color: token.colorTextSecondary
                                },
                                children: planDescription
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "checkout-v2-product-row__price",
                        children: summary.productRow.kind === "recurringCoupon" ? // Strike the per-buyer pre-coupon recurring (already
                        // RC-adjusted via Paddle's `unit`), bold the
                        // post-coupon recurring.
                        /*#__PURE__*/ _jsxs("span", {
                            className: "checkout-v2-product-row__price-change",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "checkout-v2-product-row__price-strike",
                                    "aria-hidden": true,
                                    children: formatPrice(summary.productRow.perBuyerRecurringPrice)
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "checkout-v2-product-row__price-new",
                                    children: [
                                        formatPrice(summary.productRow.couponedRecurringPrice),
                                        intervalSuffix
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "checkout-v2-product-row__permanent",
                                    style: {
                                        color: token.colorSuccess
                                    },
                                    children: __("Permanent discount")
                                })
                            ]
                        }, summary.productRow.couponedRecurringPrice) : summary.productRow.kind === "introPricing" ? // Strike the list recurring with its `/ <interval>`
                        // suffix, bold the first-term price without the
                        // suffix (the renewal disclosure below carries the
                        // recurring info). `price` stays at the list intro
                        // value when a one-time coupon is active, so the
                        // Coupon-discount row isn't double-counted in the
                        // bold figure.
                        /*#__PURE__*/ _jsxs("span", {
                            className: "checkout-v2-product-row__price-change",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "checkout-v2-product-row__price-strike",
                                    "aria-hidden": true,
                                    children: [
                                        formatPrice(summary.productRow.listRecurringPrice),
                                        intervalSuffix
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "checkout-v2-product-row__price-new",
                                    children: formatPrice(summary.productRow.price)
                                })
                            ]
                        }) : `${formatPrice(summary.productRow.price)}${intervalSuffix}`
                    })
                ]
            }),
            recurringDisclosureLine && /*#__PURE__*/ _jsx("div", {
                className: "checkout-v2-product-row__recurring",
                style: {
                    color: token.colorTextSecondary,
                    fontSize: 12,
                    marginTop: 4,
                    textAlign: "right"
                },
                children: recurringDisclosureLine
            }),
            discountLine && /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-discount-row",
                role: "status",
                "aria-live": "polite",
                style: {
                    color: token.colorSuccess
                },
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        children: __("Coupon discount")
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "checkout-v2-discount-row__amount",
                        children: discountLine
                    })
                ]
            }, summary.couponDiscountAmount),
            cashbackNoticeNode && /*#__PURE__*/ _jsx("div", {
                className: "checkout-v2-product-row__cashback",
                style: {
                    color: token.colorTextSecondary,
                    fontSize: 12,
                    marginTop: 4,
                    textAlign: "right"
                },
                children: cashbackNoticeNode
            }),
            couponExpanded && /*#__PURE__*/ _jsx("div", {
                className: `checkout-v2-soft-exit${showCouponForm ? "" : " checkout-v2-soft-exit--hidden"}`,
                "aria-hidden": !showCouponForm,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "checkout-v2-soft-exit__inner",
                    style: {
                        paddingTop: 8,
                        paddingBottom: 8
                    },
                    children: /*#__PURE__*/ _jsx(CouponForm, {})
                })
            }),
            /*#__PURE__*/ _jsx(Divider, {
                style: {
                    margin: "16px 0"
                }
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    marginBottom: 12
                },
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: taxLabel
                    }),
                    taxAmountLine && /*#__PURE__*/ _jsx("div", {
                        children: taxAmountLine
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-order-total",
                style: {
                    color: token.colorTextHeading
                },
                role: "status",
                "aria-live": "polite",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: totalLabel
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        children: formattedTotalPrice
                    })
                ]
            })
        ]
    });
});
export { OrderSummary };
